import React, { useRef } from 'react';
import { Box, Typography, Paper, Fade, Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { DataSource, ShowAs } from '../hooks/useChartData';
import UseReportingFiltersMessage from './UseReportingFiltersMessage';
import NoDataMessage from './NoDataMessage';
import LoadingResponse from '../../AIStudio/SharedComponents/Steps/LoadingResponse';
import { DownloadProgress as ProgressType } from '../services/S3DataService';

import { actionButton } from '../../NewUI/shared/styleClasses';
import ChartDataTableModal from './ChartDataTableModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

interface ChartContentProps {
  dataSource: DataSource | '';
  showAs: ShowAs | '';
  groupBy: string;
  chartData: any;
  isLoading: boolean;
  isPieChart: boolean;
  error: Error | null;
  chartTitle?: string;
  onRetry?: () => void;
  downloadProgress: ProgressType;
}

export const ChartContent: React.FC<ChartContentProps> = ({
  dataSource,
  showAs,
  groupBy,
  chartData,
  isLoading,
  isPieChart,
  error,
  chartTitle,
  onRetry,
  downloadProgress
}) => {
  const pieChartRef = useRef<ChartJS<'pie'>>(null);
  const barChartRef = useRef<ChartJS<'bar'>>(null);

  const handleExportChart = () => {
    const chart = isPieChart ? pieChartRef.current : barChartRef.current;

    if (chart && chart.ctx) {
      const image = chart.toBase64Image('image/png', 1.0);

      // Create download link
      const link = document.createElement('a');
      link.download = `chart-${dataSource}-${showAs}-${groupBy}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = image;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          boxWidth: 20,
          font: { size: 12 }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    ...(isPieChart
      ? {
          layout: {
            padding: 20
          }
        }
      : {
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: false
              }
            }
          }
        })
  };

  if (isLoading) {
    return (
      <Fade in timeout={200}>
        <Paper
          elevation={0}
          sx={{
            height: 400,
            display: 'block',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default'
          }}
        >
          <LoadingResponse
            generationType="custom"
            customMessages={[
              'Crunching the numbers',
              'Analyzing your data',
              'Building your report masterpiece',
              'Summing up the insights',
              'Generating your report',
              'Compiling the latest figures',
              'Preparing your reports'
            ]}
            showProgressBar={true}
            showProgress={downloadProgress.status !== 'idle'}
            progress={downloadProgress.percentage}
            completed={downloadProgress.completed}
            total={downloadProgress.total}
          />
        </Paper>
      </Fade>
    );
  }

  if (!dataSource || !showAs || !groupBy) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          <UseReportingFiltersMessage />
        </Box>
      </Fade>
    );
  }

  if (error) {
    // Determine if this is an S3-related error
    const isS3Error =
      ['applications', 'jobs'].includes(dataSource) &&
      (error.message?.includes('S3') ||
        error.message?.includes('download') ||
        error.message?.includes('fetch') ||
        error.message?.includes('CSV'));

    const errorMessage = isS3Error
      ? 'Failed to fetch data from S3. This could be due to network issues or the data may not be available.'
      : error.message || 'Failed to load chart data';

    return (
      <Fade in timeout={300}>
        <Paper
          elevation={0}
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 3
          }}
        >
          <Typography variant="h6" color="error" align="center" gutterBottom>
            {errorMessage}
          </Typography>

          {isS3Error && onRetry && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                You can try refreshing the data to resolve this issue.
              </Typography>
              <button
                onClick={onRetry}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Retry Data Download
              </button>
            </Box>
          )}
        </Paper>
      </Fade>
    );
  }

  if (!chartData || !chartData.labels || chartData.labels.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Fade in timeout={700}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          >
            <NoDataMessage />
          </Box>
        </Fade>
      </Box>
    );
  }

  return (
    <Box>
      <Fade in timeout={400}>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              pb: 3,
              pl: 3
            }}
          >
            {chartTitle && (
              <Typography variant="h6" sx={{ fontFamily: 'inherit', marginRight: 'auto' }}>
                {chartTitle}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {chartData && chartData?.labels?.length > 0 && chartData?.datasets?.length > 0 && (
                <ChartDataTableModal chartData={chartData} chartTitle={chartTitle} />
              )}
              <Button
                variant="outlined"
                startIcon={
                  <>
                    <DownloadIcon sx={{ color: '#4EC1C1' }} />
                  </>
                }
                onClick={handleExportChart}
                sx={{
                  ...actionButton,
                  background: '#E6F7F7',
                  color: '#4EC1C1',
                  '& svg': {
                    color: '#4EC1C1'
                  },
                  '&:hover': {
                    background: '#4EC1C1',
                    color: '#fff',
                    '& .MuiSvgIcon-root': { color: '#fff' }
                  }
                }}
              >
                Export Chart
              </Button>
            </Box>
          </Box>
          <Box sx={{ height: 500, width: '100%' }}>
            {isPieChart ? (
              <Pie data={chartData} options={chartOptions} ref={pieChartRef} />
            ) : (
              <Bar data={chartData} options={chartOptions} ref={barChartRef} />
            )}
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};
